import { Box, Button, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Text, Image, Flex, Spacer, Hide } from "@chakra-ui/react";
import { FC, useRef, useState } from "react";
import Dropzone, { useDropzone } from "react-dropzone";
import { FiUpload } from "react-icons/fi";
import ReactCrop, { centerCrop, Crop, makeAspectCrop, PixelCrop } from "react-image-crop";
import 'react-image-crop/dist/ReactCrop.css';
import { canvasPreview } from "./canvasPreview";
import { useDebounceEffect } from "./useDebounceEffect";

interface EditImageDialogProps {
    title: string,
    helperText?: string,
    onClose: any,
    onSubmit: any,
    aspectRatio: number
};

function centerAspectCrop(
    mediaWidth: number,
    mediaHeight: number,
    aspect: number,
) {
    return centerCrop(
        makeAspectCrop(
            {
                unit: '%',
                width: 100,
            },
            aspect,
            mediaWidth,
            mediaHeight,
        ),
        mediaWidth,
        mediaHeight,
    )
}

const EditImageDialog: FC<EditImageDialogProps> = ({ title, helperText, onClose, onSubmit, aspectRatio }) => {
    const [imgSrc, setImgSrc] = useState('');
    const [crop, setCrop] = useState<Crop>();
    const imgRef = useRef<HTMLImageElement>(null);
    const previewCanvasRef = useRef<HTMLCanvasElement>(null)
    const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
    const [aspect, setAspect] = useState<number | undefined>(aspectRatio);
    const [isSubmiting, setIsSubmitting] = useState<boolean>(false);

    const handleDrop = (acceptedFiles: any) => {
        if (acceptedFiles.length > 0) {
            setCrop(undefined) // Makes crop preview update between images.
            const reader = new FileReader() as any;
            reader.addEventListener('load', () =>
                setImgSrc(reader.result.toString() || ''),
            )
            reader.readAsDataURL(acceptedFiles[0])
        }
    };

    function onImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
        if (aspect) {
            const { width, height } = e.currentTarget
            setCrop(centerAspectCrop(width, height, aspect))
        }
    };

    const getImage = () => {
        return new Promise((resolve) =>
            (previewCanvasRef as any).current.toBlob((blob: any) => {
                resolve(blob);
            })
        );
    };

    const handleSubmit = async () => {
        var imageData = await getImage();
        setIsSubmitting(true);
        onSubmit(imageData, () => {
            setIsSubmitting(false);
        });
    };

    useDebounceEffect(
        async () => {
            if (
                completedCrop?.width &&
                completedCrop?.height &&
                imgRef.current &&
                previewCanvasRef.current
            ) {
                // We use canvasPreview as it's much faster than imgPreview.
                canvasPreview(
                    imgRef.current,
                    previewCanvasRef.current,
                    completedCrop,
                    1,
                    0,
                )
            }
        },
        100,
        [completedCrop]
    )

    return (
        <Modal size={'lg'} isOpen={true} onClose={onClose} trapFocus={false}>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader textAlign={"center"}>{title}</ModalHeader>
                <ModalCloseButton />
                <ModalBody pt={5}>
                    {
                        !Boolean(imgSrc) &&
                        <>
                            <Text>File type: JPG, PNG, GIF</Text>
                            <Text fontSize={'sm'} color={'blackAlpha.700'}>{helperText}</Text>
                            <Dropzone
                                onDrop={handleDrop}
                                accept={{ 'image/*': ['.png', '.jpeg', '.jpg', '.gif'] }}
                            >
                                {({ getRootProps, getInputProps }) => (
                                    <Box
                                        my={4}
                                        {...getRootProps({ className: "dropzone" })}
                                        rounded={"lg"}
                                        border="2px"
                                        borderStyle={"dashed"}
                                        borderColor={"primary.500"}
                                        box-sizing="border-box"
                                    >
                                        <Flex height={'sm'}>
                                            <Spacer />
                                            <Box as={Flex} justifyItems={"center"} alignItems={'center'}>
                                                <Box>
                                                    <Text textAlign={'center'} fontWeight={'bold'} fontSize={'xl'} mt={2}>
                                                        Drag & Drop
                                                    </Text>
                                                    <Text textAlign={'center'} mt={2}>
                                                        or
                                                    </Text>
                                                    <Button mt={2} variant={'ghost'}>
                                                        Select an image
                                                        <input {...getInputProps()} />
                                                    </Button>
                                                </Box>
                                            </Box>
                                            <Spacer />
                                        </Flex>
                                    </Box>
                                )}
                            </Dropzone>
                        </>
                    }

                    {
                        Boolean(imgSrc) && (
                            <ReactCrop
                                crop={crop}
                                onChange={(_, percentCrop) => setCrop(percentCrop)}
                                onComplete={(c) => setCompletedCrop(c)}
                                aspect={aspect}
                            >
                                <img
                                    ref={imgRef}
                                    alt="Crop me"
                                    src={imgSrc}
                                    onLoad={onImageLoad}
                                />
                            </ReactCrop>
                        )
                    }
                    {
                        Boolean(completedCrop) && (
                            <canvas
                                hidden={true}
                                ref={previewCanvasRef}
                                style={{
                                    border: '1px solid black',
                                    objectFit: 'contain',
                                    width: completedCrop?.width,
                                    height: completedCrop?.height,
                                }}
                            />
                        )
                    }
                </ModalBody>
                {
                    Boolean(imgSrc) &&
                    <ModalFooter>
                        <Button variant={'outline'} mr={3} onClick={onClose}>
                            Close
                        </Button>
                        <Button onClick={handleSubmit} isLoading={isSubmiting}>Upload</Button>
                    </ModalFooter>
                }
            </ModalContent>
        </Modal >
    );
};

export default EditImageDialog;